import React, { useRef } from 'react';
import ProductSliderHandler from 'PyzShopUi/scripts/product-slider/ProductSliderHandler';
import { isTrackingConsentGiven } from 'PyzShopUi/scripts/shop-ui/services/GoogleConsentModeService';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';

const ADD_TO_CART_EVENT_NAME = 'EECaddToCart';
const GTM_PRODUCT_DATA_ID = 'ecommerce';

interface AddToCartDialogProps {
    cartPageUrl: string;
    fullArticleName: string;
    articleAmount: number;
    articleImageUrl: string;
    recommendationApiUrl: string;
    productOverrides?: Partial<ProductOverrides>;
}
interface ECommerceObjectProduct {
    name: string;
    id: string;
    price: string;
    category: string;
    variantIds: string[];
    variant: string;
    dimension26: string;
    dimension28: string;
    dimension33: string;
    brand: string;
}
interface ECommerceObject {
    detail: {
        actionField: {
            list: string
            action: string
        }
        products: ECommerceObjectProduct[]
    };
}

interface EECproductDetailViewEvent {
    ecommerce: ECommerceObject;
}

interface ProductOverrides {
    id: string;
    price: string;
    variant: string;
}

const addToCartDialog: React.FC<AddToCartDialogProps> = props => {
    const { cartPageUrl, fullArticleName, articleAmount, articleImageUrl, productOverrides, recommendationApiUrl } = props;

    const slider = useRef(undefined);

    const { translate } = useTranslationHook();

    React.useEffect(() => {
        ProductSliderHandler.initSingle(slider.current);
    }, []);

    /**
     * Push the EECaddToCart event in the dataLayer
     */
    React.useEffect(() => {
        if (!isTrackingConsentGiven()) {
            return undefined;
        }

        const productInfo: EECproductDetailViewEvent = window.dataLayer.find(dataLayerItem => {
            return dataLayerItem.hasOwnProperty(GTM_PRODUCT_DATA_ID);
        }
        );

        if (!productInfo) {
            console.warn(`no entry with ${GTM_PRODUCT_DATA_ID} attribute found in window.dataLayer`);

            return undefined;
        }

        // create a new object from productInfo.ecommerce.detail.products[0] without variantIds
        const { variantIds, ...productToAdd } = { ...productInfo.ecommerce.detail.products[0] };

        // note the IIFE
        const quantity = (() => {
            const quantityDomElement: HTMLElement = document.querySelector('.add-to-cart-dialog-article-text');

            if (!quantityDomElement) {
                return 1;
            }

            const [q] = quantityDomElement.innerText.split('x');

            return Number(q);
        })();

        // note the IIFE
        const currencyCode = (() => {
            const currencyDomElement = document.querySelector('meta[itemprop="priceCurrency"]');

            if (!currencyDomElement) {
                console.error(`Couldn't find element for selector`);
            }

            return (currencyDomElement as unknown as { content: string }).content;
        })();

        const products = {
            ...productToAdd,
            ...(productOverrides ? productOverrides : {}),
            quantity
        };

        window.dataLayer.push(
            {
                event: ADD_TO_CART_EVENT_NAME,
                ecommerce: {
                    currencyCode,
                    add: {
                        products
                    }
                }
            }
        );
    }, []);

    return (
        <div className="grid-container add-to-cart-dialog-content" data-qa="add-to-cart-dialog">
            <div className="grid-x grid-padding-x">
                <div className="cell small-3 text-center add-to-cart-dialog-image-wrapper" data-qa="product-image">
                    <img
                        src={articleImageUrl}
                        alt={fullArticleName} />
                </div>
                <div className="cell small-9 add-to-cart-dialog-article-text"
                    data-qa="product-description">
                    {`${articleAmount} x ${fullArticleName}`}
                </div>
            </div>
            <div className="grid-x grid-padding-x grid-padding-y">
                <div className="cell small-6">
                    <button className="button button-grey" data-mdc-dialog-action="close" data-qa="close">{translate('Continue shopping', { ns: 'article-detail'})}</button>
                </div>
                <div className="cell small-6 text-right">
                    <a className="button button-red" href={cartPageUrl} data-qa="go-to-cart">{translate('Go to shopping basket', { ns: 'article-detail'})}</a>
                </div>
            </div>
            <div className="grid-x">
                <div className="cell">
                    <div ref={slider}
                        data-spryker-slider="true"
                        data-slider-type="dialog"
                        data-slider-title={translate('We recommend', { ns: 'article-list'})}
                        data-api-url={recommendationApiUrl}
                        data-qa="recommendation-slider"
                    >
                    </div>
                </div>
            </div>
            <div className="grid-x">
                <div className="cell footnote" data-qa="footnote">
                </div>
            </div>
        </div>
    );
};

export default addToCartDialog;
